export const MAP_KEYBOARD_INPUT_LABEL = 'Map keyboard controls';

const isHiddenMapKeyboardInput = (input: HTMLInputElement) =>
  input.type === 'text' &&
  input.style.width === '0px' &&
  input.style.height === '0px' &&
  input.style.opacity === '0' &&
  input.style.position === 'absolute' &&
  input.style.bottom === '0px';

const labelMapInput = (input: HTMLInputElement) => {
  if (
    isHiddenMapKeyboardInput(input) &&
    !input.getAttribute('aria-label') &&
    !input.getAttribute('aria-labelledby')
  ) {
    input.setAttribute('aria-label', MAP_KEYBOARD_INPUT_LABEL);
    return true;
  }
  return false;
};

const labelMapInputs = (root: ParentNode) => {
  root.querySelectorAll<HTMLInputElement>('input').forEach(labelMapInput);
};

const labelAddedMapInputs = (nodes: NodeList) => {
  let hasLabeledInput = false;
  nodes.forEach(node => {
    if (node instanceof HTMLInputElement) {
      hasLabeledInput = labelMapInput(node) || hasLabeledInput;
      return;
    }

    if (node instanceof Element) {
      node.querySelectorAll<HTMLInputElement>('input').forEach(input => {
        hasLabeledInput = labelMapInput(input) || hasLabeledInput;
      });
    }
  });
  return hasLabeledInput;
};

/**
 * Google Maps injects a visually hidden keyboard-navigation input. Start this
 * observer before `new google.maps.Map(...)` so the body watcher can catch the
 * provider-owned input (often appended to `document.body`), then attach the
 * map-container observer after the map exists.
 *
 * The body watcher stays connected for the lifetime of this map instance
 * (until `disconnect` is called) rather than stopping at the first match, so
 * concurrently mounted maps each keep watching for their own provider-owned
 * input instead of racing to label whichever input appears first.
 */
export const createGoogleMapKeyboardInputObserver = () => {
  let mapBodyObserver: MutationObserver | undefined;
  let mapDomObserver: MutationObserver | undefined;

  if (document.body) {
    mapBodyObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => labelAddedMapInputs(mutation.addedNodes));
    });
    mapBodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  const observeMapContainer = (mapContainer: ParentNode | null) => {
    if (!mapContainer) {
      return;
    }

    labelMapInputs(mapContainer);
    mapDomObserver = new MutationObserver(() => labelMapInputs(mapContainer));
    mapDomObserver.observe(mapContainer, { childList: true, subtree: true });
  };

  const disconnect = () => {
    mapBodyObserver?.disconnect();
    mapDomObserver?.disconnect();
  };

  return { observeMapContainer, disconnect };
};
